/**
 * 簡單的語言切換器 - 最小化 UI 影響
 */

import { SimpleI18n, Language } from './SimpleI18n.js';

export class LangSwitcher {
  private i18n: SimpleI18n;
  private container: HTMLElement | null = null;

  constructor() {
    this.i18n = SimpleI18n.getInstance();
  }

  public init(): void {
    // 檢查是否已經初始化，避免重複創建
    if (this.container && document.body.contains(this.container)) {
      console.log('[LangSwitcher] 語言切換器已存在，跳過重複初始化');
      return;
    }
    
    // 清理可能存在的舊元素（防止孤兒元素）
    this.destroy();
    
    // 在頁面右上角添加語言切換器
    this.createSwitcher();
    
    // 初始化時更新一次文字
    setTimeout(() => {
      this.i18n.updatePageTexts();
    }, 500);
  }

  private createSwitcher(): void {
    // 創建可拖曳的語言切換器
    const switcher = document.createElement('div');
    switcher.className = 'draggable-lang-switcher';
    
    // 從 localStorage 讀取保存的位置，或使用預設位置
    const savedPosition = this.getSavedPosition();
    const isMobile = window.innerWidth <= 768;
    
    switcher.style.cssText = `
      position: fixed;
      top: ${savedPosition.top}px;
      left: ${savedPosition.left}px;
      z-index: 1000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      cursor: move;
      user-select: none;
      touch-action: none;
    `;

    // 創建主按鈕（顯示當前語言）
    const currentLang = this.i18n.getSupportedLanguages().find(
      lang => lang.code === this.i18n.getCurrentLanguage()
    ) || this.i18n.getSupportedLanguages()[0];

    const mainButton = document.createElement('button');
    mainButton.className = 'lang-main-button';
    mainButton.innerHTML = `
      <span class="drag-handle">⋮⋮</span>
      <span class="lang-flag">${currentLang.flag}</span>
      <span class="lang-name">${isMobile ? '' : currentLang.name}</span>
      <span class="dropdown-arrow">▼</span>
    `;
    
    this.styleMainButton(mainButton, isMobile);

    // 創建下拉選單
    const dropdown = document.createElement('div');
    dropdown.className = 'lang-dropdown';
    dropdown.style.cssText = `
      position: absolute;
      right: 0;
      background: rgba(0, 0, 0, 0.9);
      border-radius: 12px;
      padding: 8px;
      backdrop-filter: blur(15px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
      opacity: 0;
      visibility: hidden;
      transform: translateY(-10px) scale(0.95);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      min-width: 150px;
    `;

    // 創建語言選項
    const languages = this.i18n.getSupportedLanguages();
    languages.forEach(lang => {
      const option = document.createElement('button');
      option.className = 'lang-option';
      option.setAttribute('data-lang', lang.code); // 添加語言代碼屬性
      option.innerHTML = `
        <span class="lang-flag">${lang.flag}</span>
        <span class="lang-name">${lang.name}</span>
        ${lang.code === this.i18n.getCurrentLanguage() ? '<span class="check-mark">✓</span>' : ''}
      `;
      
      this.styleLangOption(option, lang.code === this.i18n.getCurrentLanguage());

      option.addEventListener('click', (e) => {
        e.stopPropagation();
        this.switchLanguage(lang.code);
        this.updateMainButton(mainButton, lang);
        this.updateDropdownCheckmarks(dropdown); // 更新下拉選單中的勾選狀態
        this.closeDropdown(dropdown);
      });

      dropdown.appendChild(option);
    });

    // 主按鈕點擊事件（只在非拖曳時觸發）
    let isDragging = false;
    let dragStartTime = 0;
    
    mainButton.addEventListener('mousedown', (e) => {
      dragStartTime = Date.now();
      this.startDrag(e, switcher);
    });
    
    mainButton.addEventListener('touchstart', (e) => {
      dragStartTime = Date.now();
      this.startDrag(e, switcher);
    }, { passive: false });
    
    // 為手機版添加 touchend 事件處理
    mainButton.addEventListener('touchend', (e) => {
      const dragDuration = Date.now() - dragStartTime;
      const wasDragging = (switcher as any)._wasDragging;
      
      // 如果是短觸摸且沒有拖曳，觸發下拉選單
      if (!wasDragging && dragDuration < 300) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDropdown(dropdown);
        
        // 重置拖曳狀態
        (switcher as any)._wasDragging = false;
      }
    }, { passive: false });
    
    mainButton.addEventListener('click', (e) => {
      const dragDuration = Date.now() - dragStartTime;
      const wasDragging = (switcher as any)._wasDragging;
      
      // 只有在沒有拖曳或拖曳時間很短且沒有實際拖曳時才觸發點擊
      if (!wasDragging && dragDuration < 300) {
        e.stopPropagation();
        this.toggleDropdown(dropdown);
      }
      
      // 重置拖曳狀態
      (switcher as any)._wasDragging = false;
    });

    // 點擊外部關閉下拉選單
    document.addEventListener('click', () => {
      this.closeDropdown(dropdown);
    });

    switcher.appendChild(mainButton);
    switcher.appendChild(dropdown);
    document.body.appendChild(switcher);
    this.container = switcher;
    
    // 初始設定下拉選單位置
    this.setDropdownPosition(dropdown);
    
    // 監聽窗口大小變化
    window.addEventListener('resize', () => {
      this.updateDropdownPosition();
    });
  }

  private switchLanguage(language: Language): void {
    this.i18n.setLanguage(language);
    console.log(`[LangSwitcher] 語言已切換為: ${language}`);
  }

  private updateDropdownCheckmarks(dropdown: HTMLElement): void {
    // 更新下拉選單中所有語言選項的勾選狀態
    const currentLang = this.i18n.getCurrentLanguage();
    const options = dropdown.querySelectorAll('.lang-option');
    
    options.forEach((option: Element) => {
      const langCode = option.getAttribute('data-lang');
      const isActive = langCode === currentLang;
      
      // 更新勾選標記
      const existingCheckMark = option.querySelector('.check-mark');
      if (isActive && !existingCheckMark) {
        // 添加勾選標記
        const checkMark = document.createElement('span');
        checkMark.className = 'check-mark';
        checkMark.textContent = '✓';
        option.appendChild(checkMark);
      } else if (!isActive && existingCheckMark) {
        // 移除勾選標記
        existingCheckMark.remove();
      }
      
      // 更新樣式
      this.styleLangOption(option as HTMLElement, isActive);
    });
  }

  private getSavedPosition(): { top: number; left: number } {
    const saved = localStorage.getItem('lang_switcher_position');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // 忽略解析錯誤
      }
    }
    
    // 預設位置：任務 tab 右側
    const isMobile = window.innerWidth <= 768;
    return {
      top: isMobile ? 420 : 420, // 大約在 tabs 的位置
      left: isMobile ? window.innerWidth - 80 : window.innerWidth - 160
    };
  }

  private savePosition(top: number, left: number): void {
    localStorage.setItem('lang_switcher_position', JSON.stringify({ top, left }));
  }

  private startDrag(e: MouseEvent | TouchEvent, element: HTMLElement): void {
    const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const startY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const startLeft = parseInt(element.style.left, 10);
    const startTop = parseInt(element.style.top, 10);
    
    let isDragging = false;
    let hasPreventedDefault = false;
    
    const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
      const currentX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const currentY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;
      
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      
      // 如果移動距離超過閾值，開始拖曳
      if (!isDragging && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
        isDragging = true;
        
        // 只有在確定要拖曳時才阻止默認行為
        if (!hasPreventedDefault) {
          moveEvent.preventDefault();
          hasPreventedDefault = true;
        }
        
        // 添加拖曳樣式
        element.style.transition = 'none';
        element.style.transform = 'scale(1.05)';
        element.style.opacity = '0.9';
        element.style.zIndex = '1001';
      }
      
      if (isDragging) {
        const newLeft = Math.max(0, Math.min(window.innerWidth - element.offsetWidth, startLeft + deltaX));
        const newTop = Math.max(0, Math.min(window.innerHeight - element.offsetHeight, startTop + deltaY));
        
        element.style.left = newLeft + 'px';
        element.style.top = newTop + 'px';
      }
    };
    
    const handleEnd = () => {
      // 恢復樣式
      element.style.transition = 'all 0.3s ease';
      element.style.transform = 'scale(1)';
      element.style.opacity = '1';
      element.style.zIndex = '1000';
      
      if (isDragging) {
        // 保存位置
        const finalLeft = parseInt(element.style.left, 10);
        const finalTop = parseInt(element.style.top, 10);
        this.savePosition(finalTop, finalLeft);
      }
      
      // 移除事件監聽器
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      
      // 將拖曳狀態存儲到元素上，供點擊事件使用
      (element as any)._wasDragging = isDragging;
    };
    
    // 添加事件監聽器
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }

  private styleMainButton(button: HTMLElement, isMobile: boolean): void {
    button.style.cssText = `
      background: rgba(0, 0, 0, 0.85);
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 16px;
      color: white;
      cursor: move;
      padding: ${isMobile ? '12px 16px' : '10px 14px'};
      font-size: ${isMobile ? '16px' : '14px'};
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: all 0.3s ease;
      backdrop-filter: blur(15px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
      min-width: ${isMobile ? '80px' : '140px'};
      min-height: ${isMobile ? '50px' : '46px'};
      touch-action: none;
      -webkit-tap-highlight-color: transparent;
      user-select: none;
      position: relative;
      overflow: hidden;
    `;
    
    // 添加拖曳手柄樣式
    const dragHandle = button.querySelector('.drag-handle') as HTMLElement;
    if (dragHandle) {
      dragHandle.style.cssText = `
        color: rgba(255, 255, 255, 0.6);
        font-size: 12px;
        line-height: 1;
        cursor: move;
        user-select: none;
        transform: rotate(90deg);
      `;
    }

    // 懸停效果
    button.addEventListener('mouseenter', () => {
      button.style.background = 'rgba(74, 144, 226, 0.8)';
      button.style.borderColor = 'rgba(74, 144, 226, 0.8)';
      button.style.transform = 'translateY(-1px)';
    });

    button.addEventListener('mouseleave', () => {
      button.style.background = 'rgba(0, 0, 0, 0.8)';
      button.style.borderColor = 'rgba(255, 255, 255, 0.3)';
      button.style.transform = 'translateY(0)';
    });
  }

  private styleLangOption(option: HTMLElement, isActive: boolean): void {
    option.style.cssText = `
      background: ${isActive ? 'rgba(74, 144, 226, 0.2)' : 'transparent'};
      border: none;
      border-radius: 8px;
      color: white;
      cursor: pointer;
      padding: 12px 16px;
      font-size: 14px;
      font-weight: 500;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      transition: all 0.2s ease;
      width: 100%;
      margin-bottom: 4px;
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
      user-select: none;
    `;

    option.addEventListener('mouseenter', () => {
      if (!isActive) {
        option.style.background = 'rgba(255, 255, 255, 0.1)';
      }
    });

    option.addEventListener('mouseleave', () => {
      if (!isActive) {
        option.style.background = 'transparent';
      }
    });
  }

  private toggleDropdown(dropdown: HTMLElement): void {
    const isOpen = dropdown.style.opacity === '1';
    
    if (isOpen) {
      this.closeDropdown(dropdown);
    } else {
      this.openDropdown(dropdown);
    }
  }

  private openDropdown(dropdown: HTMLElement): void {
    // 重新計算最佳展開位置
    this.setDropdownPosition(dropdown);
    
    dropdown.style.opacity = '1';
    dropdown.style.visibility = 'visible';
    dropdown.style.transform = 'translateY(0) scale(1)';
    
    // 更新箭頭
    const arrow = this.container?.querySelector('.dropdown-arrow');
    if (arrow) {
      arrow.textContent = '▲';
    }
  }

  private closeDropdown(dropdown: HTMLElement): void {
    // 根據當前位置決定關閉動畫方向
    const isOpeningUpward = dropdown.style.bottom === '100%';
    const direction = isOpeningUpward ? '10px' : '-10px';
    
    dropdown.style.opacity = '0';
    dropdown.style.visibility = 'hidden';
    dropdown.style.transform = `translateY(${direction}) scale(0.95)`;
    
    // 更新箭頭
    const arrow = this.container?.querySelector('.dropdown-arrow');
    if (arrow) {
      arrow.textContent = '▼';
    }
  }

  private updateMainButton(button: HTMLElement, lang: { flag: string; name: string }): void {
    const isMobile = window.innerWidth <= 768;
    button.innerHTML = `
      <span class="drag-handle">⋮⋮</span>
      <span class="lang-flag">${lang.flag}</span>
      <span class="lang-name">${isMobile ? '' : lang.name}</span>
      <span class="dropdown-arrow">▼</span>
    `;
    
    // 重新應用拖曳手柄樣式
    const dragHandle = button.querySelector('.drag-handle') as HTMLElement;
    if (dragHandle) {
      dragHandle.style.cssText = `
        color: rgba(255, 255, 255, 0.6);
        font-size: 12px;
        line-height: 1;
        cursor: move;
        user-select: none;
        transform: rotate(90deg);
      `;
    }
  }

  private updateDropdownPosition(): void {
    if (!this.container) return;
    
    const isMobile = window.innerWidth <= 768;
    const mainButton = this.container.querySelector('.lang-main-button') as HTMLElement;
    const dropdown = this.container.querySelector('.lang-dropdown') as HTMLElement;
    
    if (mainButton && dropdown) {
      // 檢查當前位置是否仍在螢幕內
      const currentLeft = parseInt(this.container.style.left, 10);
      const currentTop = parseInt(this.container.style.top, 10);
      
      const maxLeft = window.innerWidth - this.container.offsetWidth;
      const maxTop = window.innerHeight - this.container.offsetHeight;
      
      // 調整位置以保持在螢幕內
      if (currentLeft > maxLeft) {
        this.container.style.left = Math.max(0, maxLeft) + 'px';
      }
      if (currentTop > maxTop) {
        this.container.style.top = Math.max(0, maxTop) + 'px';
      }
      
      // 重新樣式化主按鈕
      this.styleMainButton(mainButton, isMobile);
      
      // 使用統一的智能位置計算
      this.setDropdownPosition(dropdown);
      
      // 更新主按鈕內容
      const currentLang = this.i18n.getSupportedLanguages().find(
        lang => lang.code === this.i18n.getCurrentLanguage()
      );
      if (currentLang) {
        this.updateMainButton(mainButton, currentLang);
      }
      
      // 保存調整後的位置
      const finalLeft = parseInt(this.container.style.left, 10);
      const finalTop = parseInt(this.container.style.top, 10);
      this.savePosition(finalTop, finalLeft);
    }
  }

  /**
   * 智能計算並設定下拉選單位置
   * 根據按鈕在螢幕中的位置動態決定往上或往下展開
   */
  private setDropdownPosition(dropdown: HTMLElement): void {
    if (!this.container) return;
    
    const rect = this.container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // 預估下拉選單高度（語言選項數量 × 項目高度 + padding）
    const languageCount = this.i18n.getSupportedLanguages().length;
    const estimatedHeight = languageCount * 50 + 16; // 每個選項約50px + 16px padding
    
    // 智能判斷：優先往下展開，空間不足且上方空間更大時往上展開
    const shouldOpenUpward = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    
    console.log(`[LangSwitcher] 位置計算: 上方空間=${spaceAbove}px, 下方空間=${spaceBelow}px, 預估高度=${estimatedHeight}px, 往上展開=${shouldOpenUpward}`);
    
    // 設定下拉選單位置
    dropdown.style.top = shouldOpenUpward ? 'auto' : '100%';
    dropdown.style.bottom = shouldOpenUpward ? '100%' : 'auto';
    dropdown.style.marginTop = shouldOpenUpward ? '0' : '8px';
    dropdown.style.marginBottom = shouldOpenUpward ? '8px' : '0';
    
    // 更新動畫方向
    const currentTransform = dropdown.style.transform;
    if (currentTransform.includes('translateY')) {
      const direction = shouldOpenUpward ? '10px' : '-10px';
      dropdown.style.transform = currentTransform.replace(/translateY\([^)]+\)/, `translateY(${direction})`);
    }
  }

  /**
   * 重置語言切換按鈕到預設位置
   */
  public resetToDefaultPosition(): void {
    // 清除 localStorage 中的位置記錄
    localStorage.removeItem('lang_switcher_position');
    
    // 如果按鈕存在，重新設定到預設位置
    if (this.container && document.body.contains(this.container)) {
      const defaultPosition = this.getSavedPosition(); // 這會返回預設位置，因為 localStorage 已清除
      this.container.style.left = defaultPosition.left + 'px';
      this.container.style.top = defaultPosition.top + 'px';
      console.log('[LangSwitcher] 語言切換按鈕已重置到預設位置');
    }
  }

  public destroy(): void {
    // 清理當前記錄的容器
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
    
    // 額外清理：移除所有可能的孤兒語言切換器元素
    const existingSwitchers = document.querySelectorAll('.draggable-lang-switcher');
    existingSwitchers.forEach(switcher => {
      console.log('[LangSwitcher] 清理孤兒語言切換器元素');
      switcher.remove();
    });
  }
}
